import Team from './Team';
import { SPORTS_SEQUENTIAL } from '@shared/constants/game';

const properties = [
	'id',
	'name',
	'nameTranslations',
	'description',
	'imageUrl',
	'categoryParentId',
	'categoryParent',
	'categoryChildId',
	'categoryChild',
	'image',
	'images',
	'ads',
	'streams',
	'sport',
	'scores',
	'start',
	'startTimestamp',
	'endTimestamp',
	'kickoffOffsetMinutes',
	'durationSeconds',
	'liveInSeconds',
	'status',
	'isLive',
	'isEnded',
	'venue',
	'location',
	'visibility',
	'vodOnly',
	'vodStatus',
	'videoAvailable',
	'isReplacing',
	'hlsUrl',
	'url',
	'updateable',
	'productionType',
	'participants',
	'league',
	'labels',
	'periods',
	'scoreboardSettings',
	'wasMadePrivate',
	'placeholder', // bool
	'placeholderText', // string
	'playerCustomText', // string
	'playerCustomTextEnabled', // bool
	'playerCustomOverlaySettings', // object
	'audioLanguages', // array of strings
	'geoblocking', // object with whitelist/blacklist array
	'accessRestriction',
	'finalScore',
	'tagOffsetSeconds',
	'tagsVisible',
	'couchmodeAvailable',
	'productPlacementEnabled',
	'externalEventLink',
	'arenaDownloadEnabled',
	'arenaCroppingEnabled',
	'arenaFeatureAccess',
	'downloadable',
	'exclusive',
	'previewDuration',
	'isPreview',
	'isGeoblocked',
	'isPaymentProtected',
	'isLoginProtected',
	'videoProcessing',
	'externalEventUrlEnabled',
	'teamGuest',
	'teamHome',
	'type',
	'isSaved',
	'viewsTotal',
	'publishStart',
	'publishEnd',
	'publishEnded',
	'publishNotStarted',
	'prePublishTeaser',
];
const PARTICIPANT_CONTEXT_TEAM = 'team';
const PARTICIPANT_CONTEXT_PLAYER = 'player';

class Event {
	id = null;
	ads = [];
	name = null;
	nameTranslations = null;
	description = null;
	imageUrl = null;
	categoryParentId = null;
	categoryParent = null;
	categoryChildId = null;
	categoryChild = null;
	image = null;
	images = null;
	streams = null;
	periods = null;
	sport = null;
	scores = null;
	start = null;
	startTimestamp = null;
	endTimestamp = null;
	durationSeconds = null;
	liveInSeconds = null;
	status = null;
	isLive = null;
	isEnded = null;
	venue = null;
	location = null;
	visibility = null;
	vodOnly = null;
	vodStatus = null;
	videoAvailable = null;
	videoProcessing = null;
	isReplacing = null;
	hlsUrl = null;
	url = null;
	updateable = null;
	productionType = null;
	tags = [];
	participants = [];
	league = null;
	teams = [];
	labels = [];
	scoreboardSettings = null;
	placeholder = false;
	placeholderText = '';
	playerCustomText = '';
	playerCustomTextEnabled = false;
	playerCustomOverlaySettings = null;
	audioLanguages = [];
	geoblocking = null;
	accessRestriction = null;
	finalScore = null;
	tagOffsetSeconds = 0;
	tagsVisible = true;
	couchmodeAvailable = false;
	productPlacementEnabled = false;
	externalEventLink = null;
	downloadable = false;
	exclusive = false;
	previewDuration = null;
	isPreview = false;
	isGeoblocked = false;
	isPaymentProtected = false;
	isLoginProtected = false;
	externalEventUrlEnabled = null;
	teamHome = null;
	teamGuest = null;
	type = null;
	isSaved = false;
	arenaFeatureAccess = null;
	viewsTotal = 0;
	publishStart = null;
	publishEnd = null;
	prePublishTeaser = null;

	wasMadePrivate = false;

	constructor(props) {
		for (const key of properties) {
			if (key in props) {
				if (key === 'description') {
					this[key] = props.translatedDescription;
				} else {
					this[key] = props[key];
				}
			}
		}

		this.initParticipants();

		// If the teamHome and teamGuest where not loaded initially, set them from participants
		if (this.isTeamCompetition && this.teams && props.participants) {
			if (!props.teamHome) {
				this.teamHome = this.participants.filter(participant => participant.type === 'home')
					.map(participant => {
						return this.teams.find(team => team.id === participant.participatable?.id);
					})[0];
			}
			if (!props.teamGuest) {
				this.teamGuest = this.participants
					.filter(participant => participant.type === 'guest')
					.map(participant => {
						return this.teams.find(team => team.id === participant.participatable?.id);
					})[0];
			}
		}
	}

	clone() {
		const props = properties.reduce((acc, curr) => {
			acc[curr] = this[curr];
			return acc;
		}, {});

		return new Event(props);
	}

	initParticipants() {
		if (Array.isArray(this.participants) && this.participants.length > 0) {
			this.participants = this.participants.map(participant => {
				if (participant.context?.name) {
					participant.context = participant.context.name;
				}

				return participant;
			});
		}

		this.initTeams();
	}

	initTeams() {
		this.teams = [];

		if (!this.isTeamCompetition) {
			return;
		}

		this.teams = this.participants
			.filter(participant => participant.participatable)
			.map(participant => {
				return new Team(participant.participatable);
			});
	}

	teamById(teamId) {
		return this.teams.find(team => team.id === teamId);
	}

	get duration() {
		return this.durationSeconds;
	}

	get isUpcoming() {
		return this.liveInSeconds > 0;
	}

	get isPublic() {
		return this.visibility === 'public';
	}

	get isPrivate() {
		return !this.isPublic;
	}

	get isUnlisted() {
		return this.visibility === 'hidden';
	}

	get isVod() {
		return this.vodOnly;
	}

	get isLiveEvent() {
		return !this.isVod;
	}

	get liveElapsedSeconds() {
		return Date.now() / 1000 - this.startTimestamp;
	}

	get athletes() {
		return this.isPlayerCompetition ? this.participants.map(p => p.participatable) : [];
	}

	get isTeamCompetition() {
		return this.sport?.participatableContext.name === PARTICIPANT_CONTEXT_TEAM;
	}

	get isPlayerCompetition() {
		return this.sport?.participatableContext.name === PARTICIPANT_CONTEXT_PLAYER;
	}

	get isConcurrentCompetition() {
		return !this.isSequentialCompetition;
	}

	get isSequentialCompetition() {
		return SPORTS_SEQUENTIAL.includes(this.sportName);
	}

	get sportName() {
		return this.sport?.name;
	}

	get shareLinks() {
		return {
			facebook: `https://www.facebook.com/sharer/sharer.php?u=${this.url}`,
			twitter:  `https://twitter.com/intent/tweet?text=${this.url}`,
			whatsapp: `https://web.whatsapp.com/send?text=${this.url}`,
			mail:     `mailto:?subject=${encodeURIComponent(this.name)}&body=${this.url}`,
		};
	}
}

export default Event;
